import { WportHttpError } from '@wport/core';
import { InvalidArgumentError } from '../../../lib/errors';
import { printWarn } from '../../../lib/output';

/**
 * 四區塊整段取代（`company replace`）與 descriptions `video_links` 刪除（`company update`，
 * pm_38 spec §3.2.2）共用的「刪除預覽 + confirm 規則」（spec §3.2.3 第 2 點）。批 D 只消費它
 * 給 `replace.ts`；批 E 的 `company update` video_links 應沿用同一套規則，不要另外寫一份。
 */

/** request body 裡「送出的」一筆項目：只有 `enc_id` 對 diff 有意義（新項目通常省略）。 */
export interface DeletableCandidate {
	enc_id?: string | null;
}

/** GET /company 回傳的「現況」一筆項目：`enc_id` 恆存在。 */
export interface ExistingBlockItem {
	enc_id: string;
	[key: string]: unknown;
}

export interface DeletionPreviewEntry {
	enc_id: string;
	summary: string;
}

/** Read the stable machine-readable error code without relying on a localized message. */
export function getEnterpriseErrorCode(err: unknown): string | undefined {
	if (!(err instanceof WportHttpError) || !err.body || typeof err.body !== 'object') return undefined;
	const code = (err.body as Record<string, unknown>).error_code;
	return typeof code === 'string' ? code : undefined;
}

/**
 * 驗證「使用者送出的 items/video_links 陣列」本身是陣列、且每一筆都是 non-null 物件
 * （不是 array，不是字串／數字／布林等 primitive）。
 *
 * fresh-eyes F-2：`previewDeletions` 內部直接讀 `i.enc_id`，若某筆是 `null` 會丟未捕捉的
 * `TypeError`（`Cannot read properties of null`），被頂層 `exitCodeForError` 的預設分支誤判成
 * `ServerOrNetworkError`（exit 4）——這是純粹的使用者輸入錯誤，必須和其他本地輸入驗證一樣是
 * `InvalidArgumentError`（exit 2）。更隱蔽的是非物件的 primitive（例如一個裸字串）：
 * `typeof i.enc_id === 'string'` 對字串取 `.enc_id` 只會得到 `undefined`，不會拋錯，該筆會被
 * `previewDeletions` 悄悄濾掉、然後原封不動地被送進 PUT/PATCH request body——同樣是必須本地擋下
 * 的輸入錯誤，只是不會馬上炸，危害更大。
 *
 * `company replace`（`items`）與 `company update` 的 `video_links` 共用同一套規則，呼叫端在
 * 讀完 `--file`/stdin 之後、呼叫 `previewDeletions` 之前（且必須在任何網路呼叫之前）都要過這關。
 */
export function assertValidSubmittedItems(value: unknown, label: string): Record<string, unknown>[] {
	if (!Array.isArray(value)) {
		throw new InvalidArgumentError(
			`"${label}" must be a JSON array, got ${value === null ? 'null' : typeof value}`
		);
	}
	value.forEach((item, index) => {
		if (item === null || typeof item !== 'object' || Array.isArray(item)) {
			throw new InvalidArgumentError(
				`"${label}[${index}]" must be a JSON object, got ` +
					(item === null ? 'null' : Array.isArray(item) ? 'an array' : typeof item)
			);
		}
	});
	return value as Record<string, unknown>[];
}

/**
 * 現況 vs. 本次送出的項目比對出「即將被刪除」的清單：現況存在、但送出項目沒有帶同一個
 * `enc_id` 的項目。**不限 `submitted: []`**——非空但漏列一筆也算刪除（spec §3.2.3 第 2 點：
 * 「只要會移除任何一筆」）。
 *
 * 前提：`submitted` 每一筆都已是 non-null 物件（呼叫端先過 `assertValidSubmittedItems`）——
 * 本函式不再自己防禦 `null`/primitive 元素，那類輸入錯誤應在更早的地方本地擋下並回 exit 2，
 * 而不是讓這裡的 `i.enc_id` 存取意外丟出未分類的 `TypeError`。
 */
export function previewDeletions<T extends ExistingBlockItem>(
	current: readonly T[],
	submitted: readonly DeletableCandidate[],
	summarize: (item: T) => string
): DeletionPreviewEntry[] {
	const submittedIds = new Set(
		submitted.filter((i): i is { enc_id: string } => typeof i.enc_id === 'string').map((i) => i.enc_id)
	);
	return current
		.filter((item) => !submittedIds.has(item.enc_id))
		.map((item) => ({ enc_id: item.enc_id, summary: summarize(item) }));
}

/**
 * 有刪除就先印出清單（stderr，供人／agent 判讀），再依 `confirm` 決定要不要往下走。
 *
 * 本專案沒有 TTY 互動 prompt 機制（`jobs delete`／`jobs batch`／`resumes delete` 皆是
 * 「無 `--confirm` 一律本地 exit 2、不分是否為互動終端機」），刻意不新造一套 readline
 * prompt——那會偏離 Existing-First 與 YAGNI。因此這裡的規則統一為：**不論 TTY 與否，
 * 有刪除就必須顯式 `--confirm`**，滿足 spec §3.2.3 第 2 點「non-interactive 必須顯式帶
 * `--confirm`，否則 exit 2」這個可驗收的行為；沒有互動 prompt 的分支。
 */
export function enforceDeletionConfirmation(
	label: string,
	deletions: readonly DeletionPreviewEntry[],
	confirm: boolean,
	color: boolean
): void {
	if (deletions.length === 0) return;
	printWarn(
		`This replace will delete ${deletions.length} existing "${label}" item(s): ` +
			deletions.map((d) => `${d.enc_id} (${d.summary || 'untitled'})`).join('; '),
		color
	);
	if (!confirm) {
		throw new InvalidArgumentError(
			`Refusing to replace "${label}" without --confirm: ${deletions.length} existing item(s) would be deleted (see the warning above for the enc_id list). Re-run with --confirm to proceed.`
		);
	}
}

/**
 * 兩份「現況」快照（409 前 vs. 409 後重新 GET）的差異摘要，供 409 conflict 訊息使用。
 * 只比對 enc_id 集合的增減——足以讓使用者知道「誰動了它」，不需要逐欄位深比對。
 */
export function summarizeItemsDiff(
	before: readonly ExistingBlockItem[],
	after: readonly ExistingBlockItem[]
): string {
	const beforeIds = new Set(before.map((i) => i.enc_id));
	const afterIds = new Set(after.map((i) => i.enc_id));
	const added = after.filter((i) => !beforeIds.has(i.enc_id)).map((i) => i.enc_id);
	const removed = before.filter((i) => !afterIds.has(i.enc_id)).map((i) => i.enc_id);
	const parts: string[] = [];
	if (added.length > 0) parts.push(`added since your read: ${added.join(', ')}`);
	if (removed.length > 0) parts.push(`removed since your read: ${removed.join(', ')}`);
	if (parts.length === 0) parts.push('item content or order changed (enc_id set unchanged)');
	return parts.join('; ');
}
